import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Loader2, Mail, CheckCircle2, MessageSquare, Linkedin, Copy } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import MiniSparkline from './MiniSparkline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#fbbf24', '#06b6d4', '#34d399', '#22d3ee', '#fb7185', '#f97316', '#60a5fa', '#ec4899'];

/**
 * CornerstonePerformanceTrendCard
 * --------------------------------
 * Plots daily cornerstone_click counts per post over the last N days.
 * Lets the founder see which deep dives still pull traffic vs. which
 * were launch-day flashes.
 */
const CornerstonePerformanceTrendCard = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestStatus, setDigestStatus] = useState('idle');  // idle | sending | sent | error
  const [digestRecipient, setDigestRecipient] = useState(null);
  const [slackStatus, setSlackStatus] = useState('idle');  // idle | sending | sent | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/blog/cornerstone-trend?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(r.data);
      } catch (_e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const sendDigestNow = async () => {
    setDigestStatus('sending');
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/admin/cornerstone-digest/send-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDigestRecipient(r.data?.recipient || null);
      setDigestStatus(r.data?.sent ? 'sent' : 'queued');
      setTimeout(() => setDigestStatus('idle'), 6000);
    } catch (_e) {
      setDigestStatus('error');
      setTimeout(() => setDigestStatus('idle'), 6000);
    }
  };

  const sendSlackNow = async () => {
    setSlackStatus('sending');
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/admin/cornerstone-digest/slack/send-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSlackStatus(r.data?.sent ? 'sent' : 'queued');
      setTimeout(() => setSlackStatus('idle'), 6000);
    } catch (_e) {
      setSlackStatus('error');
      setTimeout(() => setSlackStatus('idle'), 6000);
    }
  };

  // ─── Founder-private cherry-pick share helpers ───────────────────────────
  // We never auto-publish funnel data. The founder picks a single line, the
  // system either copies it to clipboard or opens a pre-filled composer.
  // Nothing leaves CARTIE's surface without an explicit founder click.
  const [shareCopied, setShareCopied] = useState(false);
  const [shareHistory, setShareHistory] = useState([]);

  const _logShareAction = async (slug, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/cornerstone-share/log`,
        { slug, action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Refresh the history quietly.
      _refreshShareHistory();
    } catch (_e) { /* non-blocking */ }
  };

  const _refreshShareHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API}/admin/cornerstone-share/recent?limit=10`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShareHistory(r.data?.recent || []);
    } catch (_e) {
      setShareHistory([]);
    }
  };

  // Load history once on mount.
  useEffect(() => {
    _refreshShareHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildShareDraft = () => {
    if (!data?.series?.length) return null;
    // Find the top performer by total clicks within the visible window.
    const top = [...data.series].sort((a, b) => (b.total || 0) - (a.total || 0))[0];
    if (!top || !top.total) return null;
    return (
      `Quietly proud of this one — "${top.label}" has been the most-read deep dive on ` +
      `cartieai.com over the last ${data.window_days} days.\n\n` +
      `If you're hitting cloud-cost surprises, the playbook walks through the ` +
      `exact pattern + how to plug the leak.\n\n` +
      `https://cartieai.com/blog/${top.slug}`
    );
  };

  const handleCopyShareDraft = async () => {
    const draft = buildShareDraft();
    if (!draft) return;
    // Resolve the top slug for logging (mirrors buildShareDraft logic).
    const top = [...data.series].sort((a, b) => (b.total || 0) - (a.total || 0))[0];
    try {
      await navigator.clipboard.writeText(draft);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 4000);
      if (top?.slug) _logShareAction(top.slug, 'copy_draft');
    } catch (_e) {
      window.prompt('Copy this share draft:', draft);
      if (top?.slug) _logShareAction(top.slug, 'copy_draft');
    }
  };

  const handleOpenLinkedinComposer = () => {
    if (!data?.series?.length) return;
    const top = [...data.series].sort((a, b) => (b.total || 0) - (a.total || 0))[0];
    if (!top || !top.total) return;
    const url = `https://cartieai.com/blog/${top.slug}`;
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
    _logShareAction(top.slug, 'open_linkedin');
  };

  if (loading) {
    return (
      <Card data-testid="cornerstone-trend-card" className="bg-slate-900 border-slate-800">
        <CardContent className="p-6 flex items-center justify-center text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading performance trend…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.series || data.series.length === 0) {
    return (
      <Card data-testid="cornerstone-trend-card" className="bg-slate-900 border-slate-800">
        <CardContent className="p-6 text-center text-slate-400 text-sm">
          No cornerstone-click data yet for the trend chart.
        </CardContent>
      </Card>
    );
  }

  // Transform to recharts format: [{date, AWS: 3, SaaS: 2, ...}, ...]
  const chartData = data.dates.map((date, i) => {
    const row = { date: date.slice(5) };  // MM-DD for compactness
    data.series.forEach((s) => {
      row[s.label] = s.data[i];
    });
    return row;
  });

  return (
    <Card
      data-testid="cornerstone-trend-card"
      className="bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950/40 border border-cyan-500/30 shadow-[0_0_40px_-12px_rgba(34,211,238,0.25)]"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/30 to-cyan-600/10 border border-cyan-400/40 flex items-center justify-center shadow-inner shadow-cyan-500/20">
              <TrendingUp className="w-4 h-4 text-cyan-200" />
            </div>
            <div>
              <CardTitle className="text-base text-white tracking-tight">
                Cornerstone Performance Trend
              </CardTitle>
              <p className="text-[11px] text-cyan-300/70 mt-0.5">
                Daily reader signal across your top deep dives
              </p>
            </div>
          </div>
          <Badge className="bg-cyan-400/15 text-cyan-200 border-cyan-400/40 text-[11px] tracking-widest font-semibold px-2.5 py-1">
            LAST {data.window_days}D
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-72 rounded-lg bg-slate-950 border border-slate-800/60 p-3" data-testid="trend-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
              <XAxis
                dataKey="date"
                stroke="#cbd5e1"
                fontSize={11}
                tick={{ fill: '#cbd5e1' }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#cbd5e1"
                fontSize={11}
                tick={{ fill: '#cbd5e1' }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(15,23,42,0.98)',
                  border: '1px solid rgba(34,211,238,0.35)',
                  borderRadius: '0.5rem',
                  fontSize: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                }}
                labelStyle={{ color: '#22d3ee', fontWeight: 600 }}
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: '12px', color: '#e2e8f0' }}
                iconType="line"
              />
              {data.series.map((s, idx) => (
                <Line
                  key={s.slug}
                  type="monotone"
                  dataKey={s.label}
                  stroke={COLORS[idx % COLORS.length]}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: '#0f172a' }}
                  data-testid={`trend-line-${s.slug}`}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top contributors — colour-matched vibrant tiles with 7-day sparklines */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="trend-totals">
          {data.series.slice(0, 4).map((s, idx) => {
            const color = COLORS[idx % COLORS.length];
            // Slice last-7 + prior-7 from the series's daily data array.
            // If the window is < 14 days we fall back to half/half splits.
            const arr = Array.isArray(s.data) ? s.data : [];
            const last7 = arr.slice(-7);
            const prev7 = arr.slice(-14, -7);
            const last7Total = last7.reduce((a, b) => a + (b || 0), 0);
            const prev7Total = prev7.reduce((a, b) => a + (b || 0), 0);
            let wowDelta = null;  // null = N/A (not enough data)
            if (prev7.length >= 7) {
              if (prev7Total === 0) {
                wowDelta = last7Total > 0 ? 100 : 0;
              } else {
                wowDelta = Math.round(((last7Total - prev7Total) / prev7Total) * 100);
              }
            }
            const deltaColor =
              wowDelta == null ? '#64748b' : wowDelta >= 0 ? '#34d399' : '#fb7185';
            const deltaLabel =
              wowDelta == null
                ? '—'
                : `${wowDelta >= 0 ? '+' : ''}${wowDelta}% wow`;

            return (
              <div
                key={s.slug}
                data-testid={`trend-total-${s.slug}`}
                className="relative rounded-lg p-3 overflow-hidden border transition-all hover:scale-[1.02] hover:shadow-lg group"
                style={{
                  backgroundColor: 'rgba(15,23,42,0.7)',
                  borderColor: `${color}40`,
                  boxShadow: `inset 0 1px 0 0 ${color}25, 0 0 12px -6px ${color}40`,
                }}
              >
                <div
                  className="absolute inset-x-0 top-0 h-0.5 transition-all group-hover:h-1"
                  style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}
                />
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }}
                  />
                  <span
                    className="text-[11px] uppercase tracking-wider truncate font-semibold"
                    style={{ color: `${color}cc` }}
                  >
                    {s.label}
                  </span>
                </div>
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <div className="text-xl font-bold text-white tabular-nums leading-none">
                      {s.total}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 font-medium">
                      total clicks
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <MiniSparkline
                      data={last7.length >= 2 ? last7 : arr}
                      color={color}
                      width={68}
                      height={22}
                      testId={`trend-sparkline-${s.slug}`}
                    />
                    <span
                      data-testid={`trend-wow-${s.slug}`}
                      className="text-[11px] font-semibold tabular-nums mt-0.5"
                      style={{ color: deltaColor }}
                    >
                      {deltaLabel}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Weekly digest action — sends the founder a "top deep dive last week" email + Slack DM */}
        <div
          data-testid="cornerstone-digest-row"
          className="mt-4 rounded-lg bg-gradient-to-r from-slate-800/60 to-cyan-950/30 border border-cyan-500/20 p-3"
        >
          <div className="flex items-center gap-2.5 text-xs text-slate-200 leading-relaxed mb-3">
            <Mail className="w-4 h-4 text-cyan-300 shrink-0" />
            <span>
              <span className="text-cyan-200 font-semibold">Weekly digest:</span>{' '}
              <span className="text-slate-300">auto-sent every Monday 09:00 UTC — email + Slack.</span>
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {digestStatus === 'sent' && (
              <span
                data-testid="cornerstone-digest-success"
                className="text-[11px] text-emerald-300 font-semibold flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-400/30"
              >
                <CheckCircle2 className="w-3 h-3" />
                Email sent {digestRecipient ? `· ${digestRecipient}` : ''}
              </span>
            )}
            {digestStatus === 'queued' && (
              <span
                data-testid="cornerstone-digest-queued"
                className="text-[11px] text-amber-300 font-semibold px-2 py-1 rounded-md bg-amber-500/15 border border-amber-400/30"
              >
                Email queued (resend quota)
              </span>
            )}
            {digestStatus === 'error' && (
              <span
                data-testid="cornerstone-digest-error"
                className="text-[11px] text-rose-300 font-semibold px-2 py-1 rounded-md bg-rose-500/15 border border-rose-400/30"
              >
                Email failed
              </span>
            )}
            {slackStatus === 'sent' && (
              <span
                data-testid="cornerstone-slack-success"
                className="text-[11px] text-emerald-300 font-semibold flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-400/30"
              >
                <CheckCircle2 className="w-3 h-3" />
                Slack sent
              </span>
            )}
            {slackStatus === 'queued' && (
              <span
                data-testid="cornerstone-slack-queued"
                className="text-[11px] text-amber-300 font-semibold px-2 py-1 rounded-md bg-amber-500/15 border border-amber-400/30"
              >
                Slack demo (no webhook)
              </span>
            )}
            {slackStatus === 'error' && (
              <span
                data-testid="cornerstone-slack-error"
                className="text-[11px] text-rose-300 font-semibold px-2 py-1 rounded-md bg-rose-500/15 border border-rose-400/30"
              >
                Slack failed
              </span>
            )}
            <div className="flex-1" />
            <Button
              data-testid="cornerstone-digest-send-btn"
              size="sm"
              disabled={digestStatus === 'sending'}
              onClick={sendDigestNow}
              className="h-8 text-xs font-semibold bg-cyan-500 hover:bg-cyan-400 text-slate-950 border-0 shadow-md shadow-cyan-500/30 hover:shadow-cyan-400/50 transition-all"
            >
              {digestStatus === 'sending' ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Sending…</>
              ) : (
                <><Mail className="w-3 h-3 mr-1.5" /> Send email</>
              )}
            </Button>
            <Button
              data-testid="cornerstone-slack-send-btn"
              size="sm"
              disabled={slackStatus === 'sending'}
              onClick={sendSlackNow}
              className="h-8 text-xs font-semibold bg-cyan-500 hover:bg-cyan-400 text-white border-0 shadow-md shadow-cyan-500/30 hover:shadow-cyan-400/50 transition-all"
            >
              {slackStatus === 'sending' ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Sending…</>
              ) : (
                <><MessageSquare className="w-3 h-3 mr-1.5" /> Send Slack</>
              )}
            </Button>
          </div>

          {/* Founder-private cherry-pick share row — never auto-publishes.
              The founder reviews the draft, then chooses to copy it or open
              a LinkedIn pre-fill composer. Funnel data stays internal. */}
          <div
            data-testid="cornerstone-cherry-pick-row"
            className="mt-3 pt-3 border-t border-cyan-500/15 flex items-center justify-between gap-2 flex-wrap"
          >
            <div className="text-[11px] text-slate-400 leading-relaxed flex-1 min-w-[180px]">
              <span className="text-slate-300 font-medium">Spotted a winner?</span>{' '}
              Cherry-pick a line about the top post — you review + post manually.
              <span className="text-slate-400 italic"> Funnel data stays internal.</span>
              {(() => {
                const top = data?.series?.length
                  ? [...data.series].sort((a, b) => (b.total || 0) - (a.total || 0))[0]
                  : null;
                if (!top?.slug) return null;
                const recent = shareHistory.find((h) => h.slug === top.slug);
                if (!recent) return null;
                const when = recent.logged_at ? new Date(recent.logged_at) : null;
                const days = when
                  ? Math.max(0, Math.floor((Date.now() - when.getTime()) / (1000 * 60 * 60 * 24)))
                  : null;
                const ago = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
                return (
                  <span
                    data-testid="cornerstone-share-history-hint"
                    className="block mt-1 text-[11px] text-amber-300 font-semibold"
                  >
                    ⚠️ Already shared this one ({ago}) — pick a different angle?
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              {shareCopied && (
                <span
                  data-testid="cornerstone-share-copied"
                  className="text-[11px] text-emerald-300 font-semibold flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-400/30"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Copied to clipboard
                </span>
              )}
              <Button
                data-testid="cornerstone-share-copy-btn"
                size="sm"
                variant="outline"
                onClick={handleCopyShareDraft}
                className="h-7 text-[11px] font-medium border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                <Copy className="w-3 h-3 mr-1.5" />
                Copy draft
              </Button>
              <Button
                data-testid="cornerstone-share-linkedin-btn"
                size="sm"
                onClick={handleOpenLinkedinComposer}
                className="h-7 text-[11px] font-medium bg-[#0077B5] hover:bg-[#005f8e] text-white border-0 shadow-md shadow-blue-500/20"
              >
                <Linkedin className="w-3 h-3 mr-1.5" />
                Open LinkedIn
              </Button>
            </div>
          </div>

          {/* Founder-private share history (last 5 actions) — local memory,
              never aggregated for marketing or exposed publicly. */}
          {shareHistory.length > 0 && (
            <div
              data-testid="cornerstone-share-history"
              className="mt-2 pt-2 border-t border-slate-800/60"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                Recent shares (founder-only)
              </div>
              <ul className="space-y-1">
                {shareHistory.slice(0, 5).map((h) => {
                  const when = h.logged_at ? new Date(h.logged_at) : null;
                  const days = when
                    ? Math.max(0, Math.floor((Date.now() - when.getTime()) / (1000 * 60 * 60 * 24)))
                    : null;
                  const ago = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
                  const actionIcon = h.action === 'open_linkedin' ? '🔗' : '📋';
                  return (
                    <li
                      key={h.id}
                      className="text-[11px] text-slate-400 flex items-center gap-2 font-mono"
                    >
                      <span className="text-[11px]">{actionIcon}</span>
                      <span className="text-slate-300 truncate flex-1">{h.slug}</span>
                      <span className="text-slate-400 text-[11px]">{ago}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CornerstonePerformanceTrendCard;
